import type { TreeLookupItem } from "../types";

export type TreeSummarizationResult = {
  label: string;
  tooltipNames: string[];
  isFullSubtree: boolean;
};

type TreeIndex = {
  itemsById: Map<string, TreeLookupItem>;
  parentById: Map<string, string | undefined>;
  childrenByParentId: Map<string, string[]>;
  descendantsById: Map<string, Set<string>>;
};

const indexCache = new WeakMap<ReadonlyArray<TreeLookupItem>, TreeIndex>();

function normalizeId(id: unknown): string {
  return String(id);
}

function normalizeParentId(parentId: unknown): string | undefined {
  const raw = parentId == null ? "" : String(parentId);
  if (!raw || raw === "0") return undefined;
  return raw;
}

function getIndex(allItems: ReadonlyArray<TreeLookupItem>): TreeIndex {
  const cached = indexCache.get(allItems);
  if (cached) return cached;

  const itemsById = new Map<string, TreeLookupItem>();
  const parentById = new Map<string, string | undefined>();
  const childrenByParentId = new Map<string, string[]>();

  for (const item of allItems) {
    const id = normalizeId(item.id);
    itemsById.set(id, item);

    const parentId = normalizeParentId((item as TreeLookupItem).parentId);
    parentById.set(id, parentId);

    if (parentId) {
      const children = childrenByParentId.get(parentId) ?? [];
      children.push(id);
      childrenByParentId.set(parentId, children);
    }
  }

  const index: TreeIndex = {
    itemsById,
    parentById,
    childrenByParentId,
    descendantsById: new Map(),
  };

  indexCache.set(allItems, index);
  return index;
}

function getAllDescendantIds(rootId: string, index: TreeIndex): Set<string> {
  const cached = index.descendantsById.get(rootId);
  if (cached) return cached;

  const descendants = new Set<string>();
  if (!index.itemsById.has(rootId)) {
    index.descendantsById.set(rootId, descendants);
    return descendants;
  }

  const queue: string[] = [rootId];
  const visited = new Set<string>();

  while (queue.length > 0) {
    const currentId = queue.shift();
    if (!currentId) continue;
    if (visited.has(currentId)) continue;
    visited.add(currentId);
    descendants.add(currentId);

    const children = index.childrenByParentId.get(currentId) ?? [];
    for (const childId of children) {
      if (!visited.has(childId)) queue.push(childId);
    }
  }

  index.descendantsById.set(rootId, descendants);
  return descendants;
}

function getCandidateIds(
  selectedIdsSet: Set<string>,
  index: TreeIndex
): Set<string> {
  const candidates = new Set<string>();
  for (const selectedId of selectedIdsSet) {
    let current: string | undefined = selectedId;
    while (current) {
      if (candidates.has(current)) break;
      candidates.add(current);
      current = index.parentById.get(current);
    }
  }
  return candidates;
}

export function summarizeSelectedTreeItems(
  selectedIds: string[],
  allItems: ReadonlyArray<TreeLookupItem>,
  options: { subtreeSuffix?: string } = {}
): TreeSummarizationResult {
  if (selectedIds.length === 0) {
    return { label: "None", tooltipNames: [], isFullSubtree: false };
  }

  const index = getIndex(allItems);
  const selectedIdsSet = new Set(selectedIds.map(normalizeId));

  const candidates = getCandidateIds(selectedIdsSet, index);

  const fullySelected = new Set<string>();
  for (const candidateId of candidates) {
    const children = index.childrenByParentId.get(candidateId) ?? [];
    if (children.length === 0) continue;

    const descendants = getAllDescendantIds(candidateId, index);
    const descendantsWithoutSelf = new Set(descendants);
    descendantsWithoutSelf.delete(candidateId);
    if (descendantsWithoutSelf.size === 0) continue;

    const isEntireSubtreeSelected = [...descendantsWithoutSelf].every((id) =>
      selectedIdsSet.has(id)
    );
    if (isEntireSubtreeSelected) {
      fullySelected.add(candidateId);
    }
  }

  const summarizingRoots = [...fullySelected]
    .filter((id) => {
      const parentId = index.parentById.get(id);
      return !parentId || !fullySelected.has(parentId);
    })
    .map((id) => index.itemsById.get(id))
    .filter((item): item is TreeLookupItem => Boolean(item))
    .sort((a, b) => a.name.localeCompare(b.name));

  const coveredByRoots = new Set<string>();
  for (const root of summarizingRoots) {
    const descendants = getAllDescendantIds(normalizeId(root.id), index);
    for (const descendantId of descendants) {
      coveredByRoots.add(descendantId);
    }
  }

  const orphanSelections = [...selectedIdsSet]
    .filter((id) => !coveredByRoots.has(id))
    .map((id) => index.itemsById.get(id))
    .filter((item): item is TreeLookupItem => Boolean(item))
    .sort((a, b) => a.name.localeCompare(b.name));

  const subtreeSuffix = options.subtreeSuffix ?? " (all subcategories)";
  const tooltipNames: string[] = [
    ...summarizingRoots.map((r) => `${r.name}${subtreeSuffix}`),
    ...orphanSelections.map((o) => o.name),
  ];

  const totalGroups = summarizingRoots.length + orphanSelections.length;

  if (totalGroups === 1) {
    if (summarizingRoots.length === 1) {
      return {
        label: summarizingRoots[0].name,
        tooltipNames,
        isFullSubtree: true,
      };
    }
    return {
      label: orphanSelections[0]?.name ?? "1 selected",
      tooltipNames,
      isFullSubtree: false,
    };
  }

  return {
    label: `${totalGroups} selected`,
    tooltipNames,
    isFullSubtree: summarizingRoots.length > 0,
  };
}
